/**
 * Formulaire de création/édition de zone de prospection
 * S'affiche pendant le dessin de la zone
 */

import React, { useState } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import type { ZoneColor, Coordinate } from '../../types/prospectionZone';
import { ZONE_COLOR_LABELS, ZONE_COLOR_HEX } from '../../types/prospectionZone';
import { createProspectionZone, updateProspectionZone } from '../../services/supabase/prospectionZones.service';
import { useAuth } from '../../context/AuthContext';
import './ZoneForm.css';

interface ZoneFormProps {
  onCancel: () => void;
  onSuccess: () => void;
  coordinates?: Coordinate[];
  zone?: any; // Zone à éditer (si mode édition)
}

const ZoneForm: React.FC<ZoneFormProps> = ({
  onCancel,
  onSuccess,
  coordinates = [],
  zone
}) => {
  const { userProfile } = useAuth();
  const isEditMode = !!zone;
  
  const [name, setName] = useState(zone?.name || '');
  const [description, setDescription] = useState(zone?.description || '');
  const [color, setColor] = useState<ZoneColor>(zone?.color || 'blue');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Log des coordonnées reçues
  React.useEffect(() => {
    console.log('📊 Coordonnées reçues dans ZoneForm:', coordinates.length, 'points');
  }, [coordinates.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Le nom de la zone est requis');
      return;
    }

    // En mode création, vérifier les coordonnées
    if (!isEditMode && coordinates.length < 3) {
      setError('Veuillez dessiner une zone avec au moins 3 points');
      return;
    }

    if (!userProfile?.agency_id || !userProfile?.id) {
      setError('Informations utilisateur manquantes');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode) {
        // Mode édition : UPDATE
        const updateData: any = {
          name: name.trim(),
          description: description.trim() || undefined,
          color
        };
        
        // N'envoyer les coordonnées que si elles ont été modifiées (redessinées)
        if (coordinates.length > 0) {
          updateData.coordinates = coordinates;
          console.log('📍 Mise à jour avec nouvelles coordonnées:', coordinates.length, 'points');
        } else {
          console.log('📍 Pas de nouvelles coordonnées, on garde les existantes');
        }
        
        await updateProspectionZone(zone.id, updateData);
      } else {
        // Mode création : CREATE
        await createProspectionZone(
          {
            name: name.trim(),
            description: description.trim() || undefined,
            color,
            coordinates
          },
          userProfile.agency_id,
          userProfile.id
        );
      }

      onSuccess();
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} zone:`, err);
      setError(`Erreur lors de la ${isEditMode ? 'modification' : 'création'} de la zone`);
      setIsSubmitting(false);
    }
  };


  return (
    <div className="zone-form">
      <div className="zone-form-header">
        <h3>{isEditMode ? 'Modifier la zone' : 'Nouvelle zone de prospection'}</h3>
        <button className="zone-form-close" onClick={onCancel} disabled={isSubmitting}>
          <FaTimes />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="zone-form-content">
        {error && (
          <div className="zone-form-error">
            {error}
          </div>
        )}

        {coordinates.length >= 3 && (
          <div className="zone-form-success" style={{
            padding: '10px',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            ✓ Zone dessinée avec succès ({coordinates.length} points) ! Remplissez les informations et enregistrez.
          </div>
        )}

        <div className="zone-form-field">
          <label htmlFor="zone-name">
            Nom de la zone <span className="required">*</span>
          </label>
          <input
            id="zone-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Centre-ville, Zone prioritaire..."
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="zone-form-field">
          <label htmlFor="zone-description">Description (optionnel)</label>
          <textarea
            id="zone-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ajoutez une description..."
            rows={3}
            disabled={isSubmitting}
          />
        </div>

        <div className="zone-form-field">
          <label htmlFor="zone-color">Couleur</label>
          <div className="zone-color-selector">
            {(Object.keys(ZONE_COLOR_LABELS) as ZoneColor[]).map((colorOption) => (
              <button
                key={colorOption}
                type="button"
                className={`zone-color-option ${color === colorOption ? 'selected' : ''}`}
                style={{
                  backgroundColor: ZONE_COLOR_HEX[colorOption],
                  borderColor: color === colorOption ? '#000' : ZONE_COLOR_HEX[colorOption]
                }}
                onClick={() => setColor(colorOption)}
                disabled={isSubmitting}
                title={ZONE_COLOR_LABELS[colorOption]}
              />
            ))}
          </div>
        </div>

        <div className="zone-form-actions">
          <button
            type="button"
            className="zone-form-btn zone-form-btn-cancel"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Annuler
          </button>

          <button
            type="submit"
            className="zone-form-btn zone-form-btn-submit"
            disabled={isSubmitting || (!isEditMode && coordinates.length < 3)}
          >
            <FaCheck />
            <span>
              {isSubmitting 
                ? (isEditMode ? 'Modification...' : 'Enregistrement...') 
                : (isEditMode ? 'Modifier' : 'Enregistrer')
              }
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ZoneForm;
